import { t } from "i18next";
import { formatNumberVN } from "utils/helperFuncs";

/**
 * Chính sách vận chuyển theo giá trị đơn. Mọi dữ liệu đều của BE, FE không có giá trị mặc định:
 * - `cart.operationInfo` → ngưỡng + mức phụ phí vận hành
 * - `getShippingTypes` (GetShippingTypesV2) → danh sách hình thức, trạng thái khả dụng và thứ tự hiển thị
 */

/**
 * Giá trị đơn dùng để so với ngưỡng — tương ứng `subTotal` mà BE nhận ở `getShippingTypes`.
 * `cart` ở các nơi gọi là response thô của `useGetCart` (field BE là `subTotal`), nên đọc
 * `subTotal` trước `temporaryTotal` (chỉ có sau khi qua `convertGetCart`).
 */
export const getCartOrderValue = (cart) => Number(cart?.subTotal ?? cart?.temporaryTotal ?? 0);

/**
 * Ngưỡng + mức phụ phí vận hành — chỉ lấy từ BE, KHÔNG fallback.
 * `threshold` là `null` khi BE chưa trả, nên phía gọi phải tự xử lý trước khi hiển thị/so sánh.
 */
export const getOperationPolicy = (cart) => {
  const threshold = cart?.operationInfo?.operationFeeThreshold;

  return {
    threshold: threshold == null ? null : Number(threshold),
    fee: Number(cart?.operationInfo?.operationFee ?? 0),
  };
};

/** Phụ phí vận hành — luôn lấy từ `cart.operationFee` của BE, không tự tính ở FE. */
export const getOperationFee = (cart) => Number(cart?.operationFee ?? 0);

/**
 * Nội dung popup "PTVC đang chọn không còn hợp lệ" — dùng chung cho cả 2 nơi hiện popup
 * (real-time ở màn giỏ hàng và lúc bấm "Tạo đơn").
 * BE chưa trả ngưỡng → dùng bản message không có số, tránh in ra "từ 0đ".
 */
export const getInvalidShippingTypeMessage = ({ cart, shippingTypeName }) => {
  const { threshold } = getOperationPolicy(cart);

  if (threshold == null) {
    return t("v2.cart.shippingPolicy.invalidShippingTypeMessageNoThreshold", { name: shippingTypeName });
  }

  return t("v2.cart.shippingPolicy.invalidShippingTypeMessage", {
    name: shippingTypeName,
    threshold: formatNumberVN(threshold),
  });
};

/**
 * Chuẩn hoá response `getShippingTypes` về dạng options của Radio.Group.
 * Giữ NGUYÊN thứ tự BE trả về — thứ tự hiển thị do BE quyết định, FE không sắp xếp lại.
 * BE trả rỗng/lỗi → `[]` (không hiển thị PTVC nào), FE không tự dựng danh sách thay thế.
 */
export const getShippingTypeOptions = (shippingTypes) =>
  (shippingTypes ?? []).map((item) => ({
    value: item?.shippingType,
    label: item?.name,
    disabled: !item?.isActive,
    zone: item?.zone,
    fee: item?.fee,
    shortDescription: item?.shortDescription,
    description: item?.description,
  }));
